/**
 * `ToastHost`'s look (DESIGN_SYSTEM.md §5.2, QD8): react-toastify's layout (bottom-centre, 320 wide, full
 * width at 480 or narrower), on its kind's STATUS SOLID with that status's `onSolid` words and icons, so
 * every toast meets AA (react-toastify's success green under white words was 2.6:1). The paint is on the
 * inner View, never the Pressable (host CSS).
 */
import { Platform } from "react-native";

import { defineVariants } from "@rista/ui/library/theme/variants";
import type { StatusTone, Theme } from "@rista/ui/library/theme/theme";

/** a toast kind's status role */
export const TOAST_TONE = { info: "info", success: "success", error: "danger" } as const satisfies Record<string, StatusTone>;

const onTone = (tone: StatusTone) => (t: Theme) => ({
  toast: { backgroundColor: t.color.status[tone].solid },
  message: { color: t.color.status[tone].onSolid },
  icon: { color: t.color.status[tone].onSolid },
});

export const toastLook = defineVariants({
  slots: ["container", "press", "toast", "icon", "message", "close"],
  base: (t) => ({
    container: {
      // web: fixed to the viewport, as react-toastify's container is; native: the root view's box
      position: Platform.OS === "web" ? ("fixed" as "absolute") : "absolute",
      bottom: t.space.lg,
      start: 0,
      end: 0,
      alignItems: "center",
      zIndex: 9999,
    },
    press: { width: 320, maxWidth: "100%", marginBottom: t.space.lg },
    toast: {
      flexDirection: "row",
      alignItems: "center",
      minHeight: 64,
      padding: 14,
      borderRadius: t.radius.control,
      boxShadow: t.elevation.toast,
    },
    icon: { marginEnd: 10, flexShrink: 0 },
    message: { ...t.type.bodyLarge, flex: 1 },
    close: { position: "absolute", top: 6, end: 6, opacity: 0.7 },
  }),
  variants: {
    kind: { info: onTone("info"), success: onTone("success"), error: onTone("danger") },
    compact: {
      true: () => ({
        container: { bottom: 0 },
        press: { width: "100%", maxWidth: undefined, marginBottom: 0 },
        toast: { borderRadius: 0 },
      }),
    },
  },
  defaults: { kind: "info" },
});
